import type { FC } from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Branded, image-free cover art that FILLS its parent frame.
 *
 * No stock photos exist for these posts, so each cover is rendered from the
 * brand system (near-black base + a per-category accent glow, a fine grid, and a
 * large ghosted category word). The caller owns the frame — aspect ratio,
 * rounding, ring, and overflow:hidden — and drops this in to fill it. That lets
 * the blog cards match the realveneers `aspect-[4/3]` photo treatment exactly,
 * including hover-zoom, without a single image asset.
 */

type Size = 'sm' | 'lg';

interface CategoryTheme {
  accent: string;
  accent2: string;
  motif: string;
}

const THEMES: Record<string, CategoryTheme> = {
  Marketing: { accent: '#2563eb', accent2: '#60a5fa', motif: 'GROWTH' },
  'Web Design': { accent: '#6366f1', accent2: '#a5b4fc', motif: 'BUILD' },
  SEO: { accent: '#10b981', accent2: '#6ee7b7', motif: 'RANK' },
  Video: { accent: '#f59e0b', accent2: '#fcd34d', motif: 'STORY' },
  'AI Automation': { accent: '#a855f7', accent2: '#d8b4fe', motif: 'AUTOMATE' },
  Network: { accent: '#06b6d4', accent2: '#67e8f9', motif: 'SHOWCASE' },
  Community: { accent: '#14b8a6', accent2: '#5eead4', motif: 'CREATE' }
};

const DEFAULT_THEME: CategoryTheme = { accent: '#2563eb', accent2: '#60a5fa', motif: 'STUDIO' };

interface BlogCoverProps {
  category: string;
  /** Motif/label scale. Use 'lg' for the featured post + post hero, 'sm' for grid cards. */
  size?: Size;
  /** Optional small caption (e.g. read time), shown on 'lg'. */
  caption?: string;
}

export const BlogCover: FC<BlogCoverProps> = (props) => {
  const { category, size = 'sm', caption } = props;
  const theme = THEMES[category] || DEFAULT_THEME;
  const lg = size === 'lg';

  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        background: '#0a0a0b',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {/* Accent corner glow */}
      <Box
        sx={{
          position: 'absolute',
          top: '-35%',
          right: '-10%',
          width: '70%',
          aspectRatio: '1 / 1',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${theme.accent}66 0%, transparent 65%)`,
          filter: 'blur(55px)',
          pointerEvents: 'none'
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-40%',
          left: '-12%',
          width: '60%',
          aspectRatio: '1 / 1',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${theme.accent2}40 0%, transparent 65%)`,
          filter: 'blur(60px)',
          pointerEvents: 'none'
        }}
      />

      {/* Fine grid */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
          backgroundSize: lg ? '54px 54px' : '34px 34px',
          maskImage: 'radial-gradient(circle at 50% 50%, #000 0%, transparent 88%)',
          WebkitMaskImage: 'radial-gradient(circle at 50% 50%, #000 0%, transparent 88%)',
          pointerEvents: 'none'
        }}
      />

      {/* Ghosted category motif word */}
      <Typography
        sx={{
          position: 'absolute',
          fontFamily: '"Inter", sans-serif',
          fontWeight: 800,
          fontSize: lg ? { xs: 76, md: 132 } : { xs: 40, md: 56 },
          letterSpacing: '-0.05em',
          lineHeight: 1,
          color: 'transparent',
          WebkitTextStroke: `1px ${theme.accent}33`,
          userSelect: 'none',
          whiteSpace: 'nowrap'
        }}
      >
        {theme.motif}
      </Typography>

      {/* Foreground: category chip + wordmark */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: lg ? 1.5 : 1,
          px: 2,
          textAlign: 'center'
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            borderRadius: 999,
            border: `1px solid ${theme.accent}66`,
            background: 'rgba(10,10,11,0.35)',
            backdropFilter: 'blur(4px)'
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: lg ? 12 : 10,
              fontWeight: 600,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: theme.accent2
            }}
          >
            {category}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: lg ? { xs: 18, md: 22 } : 14,
            fontWeight: 700,
            letterSpacing: '-0.01em',
            color: 'rgba(255,255,255,0.94)',
            lineHeight: 1.1
          }}
        >
          Mystic Media Film
        </Typography>
        {lg && caption && (
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.55)'
            }}
          >
            {caption}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
